import { RFQRequestState, RFQResponseState } from "#/services/kyan/kyan.types";
import { KyanErrorType } from "#/services/shared/service.types";
import { IndexPrices } from "#/store/app/indexPrices/indexPrices";
import {
  type AccountStateByPortfolioSymbol,
  type AccountStateTimestamps,
  type PositionsByPairs,
} from "#/types/accounts";
import { PricesTableEntry } from "#/types/combo";
import { FundingRates } from "#/types/funding";
import {
  InterestRateData,
  Option,
  OptionChainEntry,
  SviData,
} from "#/types/options";
import { TransformedOrderbook } from "#/types/orderbook";
import { OpenOrdersByPairs } from "#/types/orders";
import { LastTrades } from "#/types/perps";

import { ResponseType } from "../protocol/base";
import { Subscription } from "../protocol/subscriptions";

/**
 * 👷‍♂️ => 🖥️ Channels for messages sent from the worker to the main thread.
 */
export enum Channel {
  OB = "ob",
  Perps = "perps",
  Prices = "prices",
  Optionchain = "optionchain",
  Status = "status",
  Option = "option",
  Trade = "trade",
  AccountState = "account_state",
  AccountStateTimestamp = "account_state_timestamp",
  RFQ = "rfq",
  Positions = "positions",
  ComboPrices = "combo_prices",
  Error = "error",
  ClearError = "clear_error",
  OpenOrders = "open_orders",
  Funding = "funding",
  IV = "iv",
  InterestRate = "interest_rate",
}

/**
 * 👷‍♂️ => 🖥️ Actions for messages sent from the worker to the main thread.
 */
export enum Action {
  Log = "log",
  Message = "message",
  Kill = "kill",
  Event = "event",
}

/**
 * 👷‍♂️ => 🌐 Message types for communication from the worker to the server.
 */
export enum MessageType {
  Auth = "auth",
  GetOBStateByInstruments = "get_ob_state_by_instruments",
  GetOBStateByMarket = "get_ob_state_by_market",
  GetSubscriptions = "get_subscriptions",
  Logout = "logout",
  Subscribe = "subscribe",
  Unsubscribe = "unsubscribe",
  UnsubscribeAll = "unsubscribe_all",
  Resend = "resend",
}

/**
 * 👷‍♂️ => 🖥️ WebSocket error interface for messages sent from the worker to the main thread.
 */
export interface KyanWsError {
  type: KyanErrorType.InternalServerError;
  message: string;
  data: {
    subscription: Subscription;
  };
}

export interface RfqContent {
  rfqResponses: RFQResponseState[];
  rfqRequests: RFQRequestState[];
}

/**
 * 👷‍♂️ => 🖥️ The content types that can be sent from the worker to the main thread.
 */
export type MessageContent =
  | string
  | LastTrades
  | FundingRates
  | IndexPrices
  | KyanWsError
  | TransformedOrderbook
  | Option
  | OptionChainEntry[]
  | AccountStateByPortfolioSymbol
  | AccountStateTimestamps
  | PositionsByPairs
  | OpenOrdersByPairs
  | RfqContent
  | PricesTableEntry[]
  | SviData
  | InterestRateData;

/**
 * 👷‍♂️ => 🖥️ Message interface for communication from the worker to the main thread.
 */
export interface WorkerToMainMessage {
  action: Action;
  channel: Channel;
  content?: MessageContent;
  type?: ResponseType;
  message?: string;
  success?: boolean;
}

export enum Status {
  Ready = "ready",
  Offline = "offline",
  Error = "error",
}

interface SubscribeMessage {
  type: MessageType.Subscribe;
  subscriptions: Subscription[];
}

interface UnsubscribeMessage {
  type: MessageType.Unsubscribe;
  subscriptions: Subscription[];
}

interface UnsubscribeAllMessage {
  type: MessageType.UnsubscribeAll;
}

/**
 * 🖥️ => 👷‍♂️ Message types for communication from the main thread to the worker.
 */
export type MainToWorkerMessage =
  | SubscribeMessage
  | UnsubscribeMessage
  | UnsubscribeAllMessage;
